import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { QrcodeOutlined } from "@ant-design/icons";
import { QRCodeSVG } from "qrcode.react";

function MobileLogin() {
  const { t } = useTranslation();
  const location = useLocation();
  const sectionRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [qrUrl, setQrUrl] = useState("");
  const [loginCode, setLoginCode] = useState("");
  const [expiresAt, setExpiresAt] = useState("");

  const createMobileLoginToken = async (mode = "qr") => {
    setLoading(true);
    setError("");
    try {
      const response = await axios.post(
        "/api/mobile-login/qr/create",
        { mode },
        { withCredentials: true }
      );
      setQrUrl(mode === "qr" ? response.data?.qrUrl || "" : "");
      setLoginCode(mode === "code" ? response.data?.loginCode || "" : "");
      setExpiresAt(response.data?.expiresAt || "");
    } catch (err) {
      console.error("[MobileLogin] create login token failed:", err);
      setError(t("mobileLoginCreateError"));
    } finally {
      setLoading(false);
    }
  };

  const generateQrCode = () => createMobileLoginToken("qr");
  const generateLoginCode = () => createMobileLoginToken("code");

  useEffect(() => {
    const shouldScroll =
      location.hash === "#mobile-login" ||
      location.state?.scrollToMobileLogin;

    if (!shouldScroll || !sectionRef.current) return undefined;

    const timer = window.setTimeout(() => {
      sectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 120);

    return () => window.clearTimeout(timer);
  }, [location.hash, location.state]);

  return (
    <section
      ref={sectionRef}
      id="security-mobile-login"
      className="security-mobile-login"
      aria-labelledby="security-mobile-login-heading"
      data-resource="private.settings.security"
      data-action="read"
    >
      <header className="user-settings-section security-page__section-heading">
        <h3 id="security-mobile-login-heading" className="settings-h3">
          {t("mobileLoginHeading")}
        </h3>
      </header>

      <div className="security-card security-card--mobile-login">
        <div className="security-card__lead">
          <div className="security-card__icon-wrap">
            <QrcodeOutlined className="security-card__icon" aria-hidden />
          </div>
          <div className="security-card__copy">
            <h4 className="security-card__title">{t("mobileLoginCardTitle")}</h4>
            <p className="security-card__subtitle">{t("mobileLoginCardDesc")}</p>
          </div>
        </div>

        <div className="security-mobile-login__content">
          {qrUrl ? (
            <div className="security-mobile-login__qr-wrap">
              <QRCodeSVG value={qrUrl} size={200} includeMargin />
            </div>
          ) : loginCode ? (
            <div className="security-mobile-login__code-wrap">
              <code className="security-mobile-login__code">{loginCode}</code>
            </div>
          ) : (
            <p className="security-sessions__status">{t("mobileLoginNoQrYet")}</p>
          )}

          {(qrUrl || loginCode) && (
            <p className="security-mobile-login__meta">
              {t("mobileLoginQrExpiresAt")} {new Date(expiresAt).toLocaleTimeString()}
            </p>
          )}
        </div>

        <div className="security-card__cta">
          <button
            type="button"
            className="btn-style1 btn-security-submit"
            onClick={generateQrCode}
            disabled={loading}
            data-resource="private.settings.security"
            data-action="execute"
            data-route="/api/mobile-login/qr/create"
          >
            {loading ? "..." : t("mobileLoginGenerateBtn")}
          </button>

          {qrUrl && (
            <button
              type="button"
              className="btn-style2 btn-security-submit"
              onClick={generateLoginCode}
              disabled={loading}
              data-resource="private.settings.security"
              data-action="execute"
              data-route="/api/mobile-login/qr/create"
            >
              {t("mobileLoginGenerateCodeInstead")}
            </button>
          )}

          {loginCode && (
            <button
              type="button"
              className="btn-style2 btn-security-submit"
              onClick={generateQrCode}
              disabled={loading}
              data-resource="private.settings.security"
              data-action="execute"
              data-route="/api/mobile-login/qr/create"
            >
              {t("mobileLoginShowQrInstead")}
            </button>
          )}
        </div>

        {error && <p className="security-card__alert">{error}</p>}
      </div>
    </section>
  );
}

export default MobileLogin;
